<template>
    <div ref="calendar" class="row task-calendar">
        <div ref="calendarHeader" class="row task-calendar__header">
            <div class="d-none d-lg-inline">
                <h2>Task Calendar</h2>
                <span class="text-note">Tasks are generally created by Automations</span>
            </div>
            <div class="d-none d-md-inline">
                <div v-if="showLoadingPrevious6Days" class="load-past">
                    <Spinner/>
                </div>
            </div>
            <div class="task-calendar__filter" v-click-outside="hideFilter">
                <div v-on:click="jumpedToToday()" class="task-calendar__filter-button" v-bind:class="{'task-calendar__filter-button--active' : isCurrentDayToday }">Today</div>
                <div class="">
                    <flat-pickr v-model="currentDay" v-bind:config="flatPickrConfig" v-on:on-change="jumpToDate" id="currentDay"></flat-pickr>
                </div>
                <div v-on:click="toggleShowFilter()"
                     class="task-calendar__filter-button"
                     v-bind:class="{'task-calendar__filter-button--active' : isFilterActive }"><i class="fas fa-filter"></i>
                    <span class="d-none d-sm-inline"> Filter</span>
                </div>
                <div v-show="isShowFilter" class="task-calendar__filter-popup">
                    <a href="#" v-on:click="selectNeedingAttention()">Needing attention</a><br/>
                    <a href="#" v-on:click="selectAllStates()">Select all</a> - <a href="#" v-on:click="clearAllStates()">Clear</a>
                    <ul>
                        <li v-for="(state, index) in states" v-bind:key="index"><input type="checkbox" v-model="state.checked">
                            <span class="task-icon"><span v-html="state.icon"></span></span>
                            {{state.label}}
                        </li>
                    </ul>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="d-md-none col-12 load-past load-past--mobile">
                <div v-if="showLoadingPrevious6Days" class="load-past">
                    <button class="btn btn-secondary center" v-on:click="loadPrevious6Days">Load previous 6 days</button>
                </div>
            </div>
        </div>

        <div v-if="errorInitial || errorAbove" class="infobar infobar-error">
            There was an error loading tasks for dates.
            <button v-on:click="fetchFirstPageOfTasks" v-if="errorInitial" class="btn btn-secondary">Try again</button>
            <button v-on:click="loadPrevious6Days"  v-if="errorAbove" class="btn btn-secondary">Try again</button>
        </div>

        <div ref="calendarContent" class="row row-cols-1 row-cols-md-2 row-cols-lg-3 rows-cols-xl-4">
            <DayOfTasks v-for="day in daysOfTasks"
                        v-bind:dayOfTasks="day"
                        v-bind:key="day.date"
                        v-bind:ref="'day-' + day.date"
                        v-bind:statesToShow="states"
                        v-bind:jumpedToDate="jumpedToDate === day.date"/>
        </div>

        <div v-show="loading">
            <Spinner/>
        </div>

        <div v-if="errorBelow" class="infobar infobar-error">
            There was an error loading tasks for dates.
            <button v-on:click="loadNext30Days" class="btn btn-secondary">Try again</button>
        </div>

    </div>
</template>

<script>
    import Spinner from '../components/Spinner.vue'
    import { store, mutations, getters } from '../store.js'
    import DayOfTasks from "../components/tasks/DayOfTasks";

    // Mixins
    import { pageUtilitiesMixin } from "../mixins/PageUtilitiesMixin";
    import { tasksMixin } from "../mixins/TasksMixin";

    import * as dayjs from 'dayjs'
    import FlatPickr from 'vue-flatpickr-component';
    import 'flatpickr/dist/flatpickr.css';
    import 'flatpickr/dist/themes/material_blue.css';

    export default {
        name: 'TaskCalendar',
        mixins: [ pageUtilitiesMixin, tasksMixin ],
        components: {
            DayOfTasks, Spinner, FlatPickr
        },
        data: function () {
            return {
                hostReference: "",
                daysOfTasks: null,
                from: dayjs().format('YYYY-MM-DD'),
                to: dayjs().add(30, "day").format('YYYY-MM-DD'),
                showLoadingPrevious6Days: false,
                loading: false,
                isShowFilter: false,
                canHideFilter: false,
                currentDay: dayjs().format('YYYY-MM-DD'),
                flatPickrConfig: {
                    mode: "single",
                    altInput: true,
                    altFormat: "j M Y",
                    dateFormat: "Y-m-d",
                    wrap: true,
                },
                states: [
                    { state: 'scheduled', label: "Scheduled", icon: this.stateIconForScheduledTask(), checked: true },
                    { state: 'paused', label: 'Paused', icon: this.stateIconForPausedTask({}),checked: true},
                    { state: 'notified', label: 'Notified', icon: this.stateIconForNotifiedTask({type: ''}),checked: true},
                    { state: 'doing', label: 'Doing', icon: this.stateIconForDoingTask(),checked: true},
                    { state: 'overdue', label: 'Overdue', icon: this.stateIconForOverdueTask({overdue: true}),checked: true},
                    { state: 'complete', label: 'Complete', icon: this.stateIconForCompleteTask(),checked: true},
                    { state: 'cancelled', label: 'Cancelled', icon: this.stateIconForCancelledTask(),checked: true},
                    { state: 'error', label: 'Error', icon: this.stateIconForErrorTask(),checked: true}
                ],
                statesToShow: [],
                ignoreNextScroll: false,
                errorInitial: false,
                errorAbove: false,
                errorBelow: false
            }
        },
        computed: {
            isCurrentDayToday: function() {
                return this.currentDay ===  dayjs().format('YYYY-MM-DD');
            },
            isFilterActive: function() {
                for (let state of this.states) {
                    if (!state.checked) {
                        return true;
                    }
                }
                return false;
            },
            jumpedToDate: function() {
                if (this.$route.params.year) {
                    return this.currentDay;
                } else {
                    return null;
                }
            }
        },
        methods: {
            initialiseDatesAndTasks() {
                this.updateDatesFromURL();
                this.fetchFirstPageOfTasks();
                this.ignoreNextScroll = true;
                this.scrollToTopOfWindow();
            },
            scrollToTopOfWindow: function() {
                this.$refs.calendar.scrollTo(0,0);
            },
            updateDatesFromURL: function() {
                if (this.$route.params.year && this.$route.params.month && this.$route.params.day) {
                    let fromDate = dayjs().year(parseInt(this.$route.params.year)).month(parseInt(this.$route.params.month)-1).date(parseInt(this.$route.params.day));
                    this.from = fromDate.format('YYYY-MM-DD');
                    this.to = fromDate.add(30, "day").format('YYYY-MM-DD');
                    this.currentDay = this.from;
                } else {
                    this.from = dayjs().format('YYYY-MM-DD');
                    this.to = dayjs().add(30, "day").format('YYYY-MM-DD');
                }
            },
            jumpedToToday: function() {
                this.currentDay = dayjs().format('YYYY-MM-DD');
                this.jumpToDate();
            },
            jumpToDate: function() {
                this.$nextTick(() => {
                    console.log("Jumping to date: " + this.currentDay);
                    const jump = dayjs(this.currentDay);
                    if (this.$router.currentRoute.name === 'JumpToDateOnTaskCalendar' &&
                        this.$router.currentRoute.params.year === jump.format("YYYY") &&
                        this.$router.currentRoute.params.month === jump.format("M") &&
                        this.$router.currentRoute.params.day === jump.format("D")
                    ) {
                        this.scrollToDate(this.currentDay);
                    } else {
                        this.$router.push({
                            name: 'JumpToDateOnTaskCalendar',
                            params: {
                                year: jump.format("YYYY"),
                                month: jump.format("M"),
                                day: jump.format("D"),
                            }
                        });
                        this.scrollToTopOfWindow();
                    }
                });
            },
            scrollToDate(date) {
                const el = this.$refs[`day-${date}`][0] || this.$refs[`day-${date}`]
                console.log(el);
                if (el && el.$el.scrollIntoView) {
                    el.$el.scrollIntoView({ behavior: "smooth", block: "center" })
                }
            },
            fetchTasks: function (from, to, callback, callbackError) {
                this.loading = true;
                this.$http.get('/guest-management/v1/establishments/' + store.currentEstablishmentReference + '/tasks/from/' + from + '/to/' + to, {
                    withCredentials: true
                }).then(response => {
                    callback(response.data);
                }).catch(error => {
                    console.log(error);
                    callbackError();
                }).finally(() => {
                    this.loading = false;
                })
            },
            fetchFirstPageOfTasks: function () {
                this.errorInitial = false;
                this.fetchTasks(this.from, this.to, (daysOfTasks) => {
                    this.daysOfTasks = daysOfTasks
                    // for (let dayOfTasks of daysOfTasks) {
                    //     dayOfTasks.error = true;
                    // }
                }, () => {
                    this.errorInitial = true;
                })
            },
            fetchTasksForDay: async function (date) {
                if (date === undefined) {
                    return;
                }
                var dateInCalendar = false;
                for (let dayOfTasks of this.daysOfTasks) {
                    if (dayOfTasks.date === date) {
                        console.log("FOUND!!!!!");
                        dayOfTasks.error = false;
                        dateInCalendar = true;
                        break;
                    }
                }
                if (dateInCalendar) {
                    const toDate = dayjs(date).add(1, "day").format('YYYY-MM-DD')
                    setTimeout(() => {
                        console.log("Reloading");
                        this.fetchTasks(date, toDate, (dayOfTasks) => {
                            this.daysOfTasks.replaceSorted(dayOfTasks[0], (a, b) => {
                                if (dayjs(a.date).isBefore(dayjs(b.date))) {
                                    return -1
                                } else if (dayjs(a.date).isAfter(dayjs(b.date))) {
                                    return 1
                                } else {
                                    return 0
                                }
                            })
                        }, () => {})
                    }, 2000);
                }
            },
            loadPrevious6Days: function () {
                this.errorAbove = false;
                const toDate = this.from;
                const fromDate = dayjs(this.from).subtract(6, "day").format('YYYY-MM-DD');
                this.from = fromDate;
                const oldHeight = this.$refs.calendar.scrollHeight;
                this.fetchTasks(fromDate, toDate, (daysOfTasks) => {
                    this.daysOfTasks.unshift(...daysOfTasks);
                    this.$nextTick(() => {
                        const newScrollHeight = this.$refs.calendar.scrollHeight;
                        // Adjust scrollTop by the difference
                        this.$refs.calendar.scrollTop += (newScrollHeight - oldHeight) - 63;
                    });
                }, () => {
                    this.errorAbove = true;
                    const fromDate = dayjs(this.from).add(6, "day").format('YYYY-MM-DD');
                    this.from = fromDate;
                })
            },
            loadNext30Days: function () {
                if (!this.loading) {
                    this.errorBelow = false;
                    const fromDate = this.to;
                    const toDate = dayjs(this.to).add(30, "day").format('YYYY-MM-DD');
                    this.to = toDate;
                    this.fetchTasks(fromDate, toDate, (daysOfTasks) => {
                        this.daysOfTasks.push(...daysOfTasks);
                    }, () => {
                        this.errorBelow = true;
                        const toDate = dayjs(this.to).subtract(30, "day").format('YYYY-MM-DD');
                        this.to = toDate;
                    })
                }
            },
            scrollHandler: function () {
                console.log('In scroll handler');
                if (this.ignoreNextScroll) {
                    this.ignoreNextScroll = false;
                    return;
                }

                let topOfWindow = this.$refs.calendar.scrollTop === 0;

                setTimeout(() => {
                    if (topOfWindow) {
                        this.showLoadingPrevious6Days = true;
                        this.loadPrevious6Days();
                    } else {
                        this.showLoadingPrevious6Days = false;
                    }
                }, 500);

                let bottomOfWindow = this.isScrolledToBottom(this.$refs.calendar);
                if (bottomOfWindow) {
                    console.log('End of scrollable area');
                    this.loadNext30Days();
                }

                if (this.$refs.calendar.scrollTop > 0) {
                    this.$refs.calendarHeader.classList.add('task-calendar__header--fixed');
                    this.$refs.calendarContent.classList.add('task-calendar--scrolled');
                } else {
                    this.$refs.calendarHeader.classList.remove('task-calendar__header--fixed');
                    this.$refs.calendarContent.classList.remove('task-calendar--scrolled');
                }
            },
            toggleShowFilter: function() {
                this.isShowFilter = !this.isShowFilter;
                // only allow outside click events once the popup is showing
                this.canHideFilter = false;
                setTimeout(() => {
                    this.canHideFilter = true;
                }, 200);
            },
            hideFilter: function() {
                if (this.canHideFilter) {
                    this.isShowFilter = false;
                    this.canHideFilter = false
                }
            },
            selectNeedingAttention: function() {
                for (let state of this.states) {
                    if (state.state === 'paused' || state.state === 'overdue') {
                        state.checked = true;
                    } else {
                        state.checked = false;
                    }
                }
            },
            selectAllStates: function() {
                for (let state of this.states) {
                    state.checked = true;
                }
            },
            clearAllStates: function() {
                for (let state of this.states) {
                    state.checked = false;
                }
            }
            // ,
            // fetchHostAlerts: function(callback) {
            //     console.log('Getting host alerts')
            //     this.$http.get('/guest-management/v1/users/' + store.host.reference + '/alerts').then(response => {
            //         if (response.data) {
            //             mutations.setHostAlerts(response.data)
            //         }
            //         callback(response.data)
            //     }).catch(error => {
            //         console.log(error)
            //     })
            // },
            // checkForWelcomeTour: function() {
            //     if (store.host.alerts != undefined && store.host.alerts.length > 0) {
            //         const welcome = store.host.alerts.find(n => n.type === 'welcome')
            //         if (welcome !== undefined) {
            //             this.$root.$emit('show-tour')
            //         }
            //     }
            // }
        },
        watch: {
            '$route.params': {
                handler: function() {
                    this.initialiseDatesAndTasks();
                },
            }
        },
        mounted () {
            // this.hostReference = this.$route.query.hr
            if (this.$route.query.er !== undefined && this.$route.query.er !== "") {
                mutations.setCurrentEstablishmentReference(this.$route.query.er)
            }

            if (getters.isAuthenticated()) {
                this.initialiseDatesAndTasks();
            }

            this.$refs.calendar.addEventListener("scroll", this.scrollHandler);

            this.$root.$on('calendar-jump-top', this.scrollToTopOfWindow);
            this.$root.$on('task-added', this.fetchTasksForDay);
            this.$root.$on('selected-establishment', this.fetchFirstPageOfTasks);

            // this.fetchHostAlerts(this.checkForWelcomeTour)
        },
        beforeDestroy () {
            this.$root.$off('calendar-jump-top', this.scrollToTopOfWindow);
            this.$root.$off('task-added', this.fetchTasksForDay);
            this.$root.$off('selected-establishment', this.fetchFirstPageOfTasks);
            window.removeEventListener("scroll", this.scrollHandler);
        }
    }
</script>

<style lang="scss" scoped>
    @import "../styles/pages/task_calendar";
    @import "../styles/components/task_states";

    .task-icon {
        ::v-deep {
            .task-state {
                position: initial;
                font-size: 0.8rem;
                margin-right: 3px;
            }
        }
    }
</style>

<!--These are overrides for the flatpickr inputs -->
<style lang="scss">
    @import "../styles/overrides/flatpickr";
    @import "../styles/overrides/flatpickr_task_calendar";
</style>
